import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Mail, Send, CheckCircle2 } from 'lucide-react';

const ROLES = ['investor', 'social_actor', 'project_manager', 'administrator', 'researcher', 'other'];
const EMPTY = { name: '', organisation: '', email: '', role: 'other', message: '' };

const ContactSection = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.trim().length < 10) {
      toast.error(t('landing.contact.validation'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail || t('landing.contact.error');
      toast.error(typeof detail === 'string' ? detail : t('landing.contact.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="py-20 md:py-24 bg-slate-50/60 border-t border-slate-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-emerald-700 text-sm font-semibold tracking-wider uppercase mb-3 inline-flex items-center gap-2">
              <Mail className="w-4 h-4" /> {t('landing.contact.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              {t('landing.contact.title')}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t('landing.contact.subtitle')}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />{t('landing.contact.bullet1')}</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />{t('landing.contact.bullet2')}</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />{t('landing.contact.bullet3')}</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="contact-form-card">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-700" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{t('landing.contact.success_title')}</h3>
                <p className="text-sm text-slate-600 mb-5">{t('landing.contact.success_body')}</p>
                <Button variant="outline" onClick={() => setSubmitted(false)} data-testid="contact-send-another">
                  {t('landing.contact.send_another')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="contact-name">{t('landing.contact.field_name')} *</Label>
                  <Input id="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} maxLength={120} data-testid="contact-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contact-org">{t('landing.contact.field_organisation')}</Label>
                    <Input id="contact-org" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} maxLength={200} data-testid="contact-organisation" />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">{t('landing.contact.field_email')} *</Label>
                    <Input id="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
                  </div>
                </div>
                <div>
                  <Label>{t('landing.contact.field_role')}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="contact-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{t(`landing.contact.role_${r}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="contact-msg">{t('landing.contact.field_message')} *</Label>
                  <Textarea id="contact-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} minLength={10} maxLength={2000} placeholder={t('landing.contact.message_placeholder')} data-testid="contact-message" />
                  <p className="text-xs text-slate-500 mt-1">{form.message.length}/2000</p>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"
                  data-testid="contact-submit"
                >
                  {loading
                    ? <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />{t('landing.contact.sending')}</span>
                    : <span className="inline-flex items-center gap-2"><Send className="w-4 h-4" /> {t('landing.contact.submit')}</span>}
                </Button>
                <p className="text-xs text-slate-500 text-center">{t('landing.contact.privacy')}</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
